import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppShell,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Image,
  MantineProvider,
  Text,
} from '@mantine/core';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PublicHomePage from './pages/PublicHomePage';
import DashboardPage from './pages/DashboardPage';
import JobDetailPage from './pages/JobDetailPage';
import CreateJobPage from './pages/CreateJobPage';
import EditJobPage from './pages/EditJobPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import MyPostedJobsPage from './pages/MyPostedJobsPage';
import ProfilePage from './pages/ProfilePage';
import ApprovalsPage from './pages/ApprovalsPage';
import ApplicantsPage from './pages/ApplicantsPage';
import ModeratorStatsPage from './pages/ModeratorStatsPage';
import UsersPage from './pages/UsersPage';
import { theme } from './theme';
import api from './services/api';

const TopNav: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [pendingApprovals, setPendingApprovals] = useState(0);

  const formatRole = (role?: string) => {
    if (role === 'JOB_SEEKER') return t('common.roles.jobSeeker');
    if (role === 'JOB_POSTER') return t('common.roles.jobPoster');
    if (role === 'MODERATOR') return t('common.roles.moderator');
    if (role === 'ADMIN') return t('common.roles.admin');
    return role || '';
  };

  const navLinks: { to: string; label: string }[] = [];

  // Add role-specific nav links
  if (user?.user_type === 'JOB_SEEKER') {
    navLinks.push({ to: '/applications/me', label: t('nav.applications') });
  } else if (user?.user_type === 'JOB_POSTER') {
    navLinks.push(
      { to: '/jobs/mine', label: t('nav.myJobs') },
      { to: '/applicants', label: t('nav.postulations') },
      { to: '/jobs/create', label: t('nav.postJob') }
    );
  }

  const adminLinks = user && ['ADMIN', 'MODERATOR'].includes(user.user_type)
    ? [
      { to: '/approvals', label: t('nav.approvals') },
      { to: '/users', label: t('nav.users') },
      ...(user.user_type === 'MODERATOR' ? [{ to: '/stats', label: t('nav.stats') }] : []),
    ]
    : [];

  useEffect(() => {
    let intervalId: number | undefined;

    const getCount = (response: unknown) => {
      if (Array.isArray(response)) return response.length;
      if (response && typeof response === 'object') {
        const results = (response as { results?: unknown[] }).results;
        if (Array.isArray(results)) return results.length;
      }
      return 0;
    };

    const loadPendingCounts = async () => {
      if (!user || !['ADMIN', 'MODERATOR'].includes(user.user_type)) {
        setPendingApprovals(0);
        return;
      }
      try {
        const [usersResponse, jobsResponse] = await Promise.all([
          api.getPendingUsers(),
          api.getPendingJobs(),
        ]);
        const usersCount = getCount(usersResponse);
        const jobsCount = getCount(jobsResponse);
        setPendingApprovals(usersCount + jobsCount);
      } catch (error) {
        setPendingApprovals(0);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadPendingCounts();
      }
    };

    loadPendingCounts();
    intervalId = window.setInterval(loadPendingCounts, 20000);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  return (
    <Box px="md" py="xs">
      <Group justify="space-between" align="center" gap="md">
        <Group gap="sm" align="center" style={{ flex: 1 }}>
          <Button
            component={Link}
            to={isAuthenticated ? '/jobs' : '/'}
            color="orange"
            size="sm"
            fw={800}
            px="sm"
            py={6}
          >
            {isAuthenticated ? (
              t('nav.brand')
            ) : (
              <Image src="/bolsa_banner3.png" alt={t('publicHome.logoAlt')} w={140} fit="contain" />
            )}
          </Button>
          {isAuthenticated && (
            <Group gap="xs" wrap="wrap">
              {navLinks.map((link) => (
                <Button
                  key={link.to}
                  component={Link}
                  to={link.to}
                  variant="subtle"
                  color="plum"
                  size="sm"
                >
                  {link.label}
                </Button>
              ))}
              {adminLinks.map((link) => (
                <Button
                  key={link.to}
                  component={Link}
                  to={link.to}
                  variant="subtle"
                  color="plum"
                  size="sm"
                  fw={600}
                  rightSection={
                    link.to === '/approvals' && pendingApprovals > 0 ? (
                      <Badge size="xs" color="orange" variant="filled">
                        {pendingApprovals}
                      </Badge>
                    ) : undefined
                  }
                >
                  {link.label}
                </Button>
              ))}
            </Group>
          )}
        </Group>
        <Group gap="sm" align="center" wrap="wrap" style={{ flex: 1, justifyContent: 'flex-end' }}>
          {isAuthenticated && user ? (
            <Group gap="xs" align="center">
              <Badge color="plum" variant="light">{formatRole(user.user_type)}</Badge>
              <Button component={Link} to="/profile" variant="subtle" color="plum" size="sm">
                {t('nav.profile')}
              </Button>
              <Button onClick={logout} color="orange" variant="light" size="sm">
                {t('nav.logout')}
              </Button>
            </Group>
          ) : (
            <Group gap="xs">
              <Button component={Link} to="/login" variant="outline" color="plum" size="sm">
                {t('nav.login')}
              </Button>
              <Button component={Link} to="/register" color="orange" variant="filled" size="sm">
                {t('nav.register')}
              </Button>
            </Group>
          )}
        </Group>
      </Group>
    </Box >
  );
};

const AppLayout: React.FC = () => {
  const footerHeight = 78;
  const headerHeight = 78;
  const { t } = useTranslation();
  const location = useLocation();
  const isUsersPage = location.pathname.startsWith('/users');
  const isHome = location.pathname === '/';

  return (
    <AuthProvider>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <AppShell
          padding="lg"
          header={{ height: { base: 130, sm: headerHeight } }}
          footer={{ height: footerHeight }}
          withBorder={false}
          styles={{
            header: {
              background: '#FFFFFF',
            },
            main: {
              background: isHome
                ? 'linear-gradient(180deg, #FFE7D6 0%, #FFF1E8 55%, #F2F2F2 100%)'
                : '#F2F2F2',
              minHeight:
                'calc(100vh - var(--app-shell-footer-height, 0px) - var(--app-shell-header-height, 0px))',
            },
          }}
        >
          <AppShell.Header>
            <TopNav />
          </AppShell.Header>
          <AppShell.Main>
            <Container size={isUsersPage ? 'xl' : 'lg'} fluid={isUsersPage}>
              <Routes>
                <Route path="/" element={<PublicHomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/jobs" element={<DashboardPage />} />
                  <Route path="/jobs/:id" element={<JobDetailPage />} />
                  <Route path="/jobs/:id/edit" element={<EditJobPage />} />
                  <Route path="/jobs/:id/applications" element={<ApplicantsPage />} />
                  <Route path="/jobs/create" element={<CreateJobPage />} />
                  <Route path="/applications/me" element={<MyApplicationsPage />} />
                  <Route path="/jobs/mine" element={<MyPostedJobsPage />} />
                  <Route path="/applicants" element={<ApplicantsPage />} />
                  <Route path="/stats" element={<ModeratorStatsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/approvals" element={<ApprovalsPage />} />
                  <Route path="/users" element={<UsersPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Container>
          </AppShell.Main>
          <AppShell.Footer>
            <Box px="md" py="xs">
              <Container size="lg">
                <Group justify="space-between" align="center" gap="md" wrap="wrap">
                  <Text c="dimmed" size="sm">
                    {t('publicHome.footerText')}
                  </Text>
                  <Group gap="xs" align="center" wrap="wrap">
                    <Text c="dimmed" size="sm">
                      {t('publicHome.footerEmailLabel')}
                    </Text>
                    <Anchor href={`mailto:${t('publicHome.footerEmail')}`} size="sm" c="plum.6">
                      {t('publicHome.footerEmail')}
                    </Anchor>
                    <Image src="/logo.png" alt={t('publicHome.logoAlt')} w={110} fit="contain" />
                  </Group>
                </Group>
              </Container>
            </Box>
          </AppShell.Footer>
        </AppShell>
      </MantineProvider>
    </AuthProvider>
  );
};

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
